import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { accountAnomalies, clusterScan, type AnomalyReport, type Cluster } from '../utils/analysis'
import { normalizeHandle } from '../utils/handle'
import { useSettings } from '../store/settings'

type Tab = 'anomalies' | 'clusters' | 'export'

export function Analysis() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('anomalies')

  return (
    <main className="mx-auto mt-4 max-w-6xl px-4 pb-16">
      <div className="glass p-5">
        <div className="mb-2 flex items-baseline justify-between">
          <h1 className="text-xl font-extrabold tracking-tight">Analysis dashboard</h1>
          <button
            type="button"
            className="glass-button text-xs"
            onClick={() => navigate('/')}
            title="close"
          >
            ✕ close
          </button>
        </div>
        <p className="mb-4 text-sm text-ink-200">
          Defensive analytics on what you've already pulled. All three tools work entirely on
          the IndexedDB cache — no requests fire when you switch tabs.
        </p>

        <div className="flex flex-wrap gap-1 rounded-xl border border-white/10 bg-white/5 p-1 text-sm">
          {(['anomalies', 'clusters', 'export'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`flex-1 rounded-lg px-3 py-1.5 transition ${
                tab === t ? 'bg-white/10 text-white' : 'text-ink-200 hover:bg-white/5'
              }`}
              style={tab === t ? { boxShadow: 'inset 0 0 0 1px var(--accent-strong)' } : undefined}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {tab === 'anomalies' ? <AnomaliesTab /> : null}
      {tab === 'clusters' ? <ClustersTab /> : null}
      {tab === 'export' ? <ExportTab /> : null}
    </main>
  )
}

function AnomaliesTab() {
  const service = useSettings((s) => s.service)
  const [handle, setHandle] = useState('')
  const [report, setReport] = useState<AnomalyReport | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function run() {
    setBusy(true)
    setError(null)
    setReport(null)
    try {
      const h = normalizeHandle(handle, service)
      if (!h) throw new Error('handle required')
      setReport(await accountAnomalies(h))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="glass mt-3 p-4">
      <div className="flex flex-wrap gap-2">
        <input
          className="glass-input flex-1"
          placeholder="handle to scan"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
        />
        <button
          type="button"
          className="glass-button glass-button--accent"
          onClick={run}
          disabled={busy || !handle}
        >
          {busy ? 'scanning…' : 'scan'}
        </button>
      </div>
      {error ? (
        <div className="mt-2 text-xs text-rose-200">{error}</div>
      ) : null}
      {report ? <AnomalyDisplay r={report} /> : null}
    </div>
  )
}

function AnomalyDisplay({ r }: { r: AnomalyReport }) {
  const max = Math.max(1, ...r.hourBuckets)
  return (
    <div className="mt-4 grid gap-3 lg:grid-cols-2">
      <div className="glass-tinted p-3 text-sm">
        <div className="text-xs uppercase tracking-widest text-ink-300">totals</div>
        <div className="mt-1">
          <strong>{r.totalActions.toLocaleString()}</strong> actions cached
        </div>
        <ul className="mt-2 grid grid-cols-2 gap-1 font-mono text-xs text-ink-200">
          <li>posts: {r.posts.toLocaleString()}</li>
          <li>replies: {r.replies.toLocaleString()}</li>
          <li>likes: {r.likes.toLocaleString()}</li>
          <li>reposts: {r.reposts.toLocaleString()}</li>
          <li>sub-second gaps: {r.subSecondBursts.toLocaleString()}</li>
          <li>sub-minute gaps: {r.subMinuteBursts.toLocaleString()}</li>
          <li>actions at .000ms: {r.zeroMsTimestamps.toLocaleString()}</li>
          <li>median gap: {r.medianGapMs.toLocaleString()}ms</li>
          <li>mean gap: {r.meanGapMs.toLocaleString()}ms</li>
          <li>earliest: {r.earliest?.slice(0, 19) ?? '—'}</li>
          <li>latest: {r.latest?.slice(0, 19) ?? '—'}</li>
        </ul>
      </div>
      <div className="glass-tinted p-3 text-sm">
        <div className="mb-2 text-xs uppercase tracking-widest text-ink-300">
          hour-of-day (UTC)
        </div>
        <div className="grid grid-cols-1 gap-1">
          {r.hourBuckets.map((n, h) => (
            <div key={h} className="flex items-center gap-2 text-[10px]">
              <span className="w-6 text-right font-mono">{String(h).padStart(2, '0')}</span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/5">
                <div
                  className="h-2 rounded-full"
                  style={{ width: `${(n / max) * 100}%`, background: 'var(--accent-strong)' }}
                />
              </div>
              <span className="w-12 text-right font-mono text-ink-300">
                {n.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function ClustersTab() {
  const [epsilonMs, setEpsilonMs] = useState(2000)
  const [minAccounts, setMinAccounts] = useState(3)
  const [targetDid, setTargetDid] = useState('')
  const [clusters, setClusters] = useState<Cluster[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function run() {
    setBusy(true)
    setError(null)
    try {
      const c = await clusterScan({ epsilonMs, minAccounts, targetDid: targetDid || undefined })
      setClusters(c)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="glass mt-3 p-4">
      <div className="grid gap-2 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
          window (ms)
          <input
            type="number"
            min={50}
            max={60_000}
            className="glass-input"
            value={epsilonMs}
            onChange={(e) => setEpsilonMs(Number(e.target.value) || 2000)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
          min accounts
          <input
            type="number"
            min={2}
            max={50}
            className="glass-input"
            value={minAccounts}
            onChange={(e) => setMinAccounts(Number(e.target.value) || 3)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
          limit to target DID (optional)
          <input
            className="glass-input font-mono text-xs"
            placeholder="did:plc:…"
            value={targetDid}
            onChange={(e) => setTargetDid(e.target.value)}
          />
        </label>
      </div>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          className="glass-button glass-button--accent"
          onClick={run}
          disabled={busy}
        >
          {busy ? 'scanning…' : 'scan IDB cache'}
        </button>
      </div>
      <p className="mt-2 text-[11px] text-ink-400">
        Scans every cached like + repost across every handle in your IDB. Slides a
        window of `window ms` and reports any subject URI where ≥`min accounts` distinct
        actors interacted within the window. Score = actors / seconds; higher = tighter
        burst with more participants.
      </p>
      {error ? <div className="mt-2 text-xs text-rose-200">{error}</div> : null}
      {clusters ? (
        <div className="mt-3 max-h-[60vh] overflow-y-auto">
          {clusters.length === 0 ? (
            <div className="text-sm text-ink-300">No clusters matched.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-ink-400">
                <tr>
                  <th className="p-1 text-left">score</th>
                  <th className="p-1 text-left">subject</th>
                  <th className="p-1 text-left">actors</th>
                  <th className="p-1 text-left">span</th>
                  <th className="p-1 text-left">first → last</th>
                </tr>
              </thead>
              <tbody>
                {clusters.slice(0, 200).map((c, i) => (
                  <tr key={i} className="border-t border-white/5 align-top">
                    <td className="p-1 font-mono">{c.score.toFixed(1)}</td>
                    <td className="p-1 font-mono text-[10px]" title={c.subjectUri}>
                      {c.subjectUri.slice(-30)}
                    </td>
                    <td className="p-1 font-mono">{c.actorCount}</td>
                    <td className="p-1 font-mono">{c.spanMs.toLocaleString()}ms</td>
                    <td className="p-1 font-mono text-[10px]">
                      {c.firstAt.slice(11, 23)} → {c.lastAt.slice(11, 23)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}
    </div>
  )
}

function ExportTab() {
  const [busy, setBusy] = useState(false)
  const [info, setInfo] = useState<string | null>(null)

  async function exportCsv() {
    setBusy(true)
    setInfo(null)
    try {
      const { db } = await import('../cache/db')
      const lines: string[] = ['handle,collection,rkey,uri,createdAt,subject']
      let n = 0
      await db.records.toCollection().each((r) => {
        const v = r.value as { createdAt?: string; subject?: { uri?: string } | string }
        const created = typeof v?.createdAt === 'string' ? v.createdAt : ''
        let subject = ''
        if (typeof v?.subject === 'string') subject = v.subject
        else if (v?.subject && typeof v.subject === 'object') subject = v.subject.uri ?? ''
        lines.push(
          [r.handle, r.collection, r.rkey, r.uri, created, subject]
            .map((s) => `"${String(s).replace(/"/g, '""')}"`)
            .join(','),
        )
        n++
      })
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `bskydisc-records-${Date.now()}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 5000)
      setInfo(`exported ${n.toLocaleString()} rows`)
    } catch (e) {
      setInfo(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="glass mt-3 p-4 text-sm">
      <p className="mb-2">
        Dump every cached record to CSV — handle, collection, rkey, uri, createdAt, subject.
        Useful for downstream Pandas / Sigma / SIEM ingestion.
      </p>
      <button
        type="button"
        className="glass-button glass-button--accent"
        onClick={exportCsv}
        disabled={busy}
      >
        {busy ? 'exporting…' : 'export CSV'}
      </button>
      {info ? <div className="mt-2 text-xs text-ink-200">{info}</div> : null}
    </div>
  )
}
